import React from "react";
import { useQuery } from "react-query";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import QrCode2RoundedIcon from "@mui/icons-material/QrCode2Rounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ZoomInRoundedIcon from "@mui/icons-material/ZoomInRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { toast } from "react-toastify";
import apiClient from "services/api/client";

const FONT = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const MONO =
  '"SF Mono", "Roboto Mono", Menlo, Consolas, "Liberation Mono", monospace';

const UI = {
  brand: "#2261A2",
  brandSoft: "#EEF4FB",
  ink: "#0F172A",
  body: "#334155",
  muted: "#64748B",
  line: "#E2E8F0",
  surface: "#F8FAFC",
};

/** Brand World's receiving account + QR (edited by admins in BXI-admin). */
export const useBrandWorldPaymentDetails = () =>
  useQuery(
    ["brand-world-payment-details"],
    () =>
      apiClient
        .get("aggregator/brand-world/payment-details")
        .then((res) => res.data?.data || { configured: false }),
    { staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false },
  );

const copy = async (label, value) => {
  try {
    await navigator.clipboard.writeText(String(value));
    toast.success(`${label} copied`);
  } catch (e) {
    toast.error("Could not copy — select and copy it manually");
  }
};

// 37392266277 -> "3739 2266 277" for reading; the raw value is what gets copied.
const groupDigits = (v) =>
  /^\d{8,}$/.test(String(v || "")) ? String(v).replace(/(\d{4})(?=\d)/g, "$1 ") : v;

const CopyButton = ({ label, value }) => (
  <Tooltip title={`Copy ${label}`} arrow>
    <IconButton
      size="small"
      onClick={() => copy(label, value)}
      aria-label={`Copy ${label}`}
      sx={{
        flexShrink: 0,
        width: 32,
        height: 32,
        borderRadius: "8px",
        color: UI.brand,
        bgcolor: UI.brandSoft,
        "&:hover": { bgcolor: "#DCE8F6" },
      }}
    >
      <ContentCopyRoundedIcon sx={{ fontSize: 16 }} />
    </IconButton>
  </Tooltip>
);

/** Label on top, value underneath — never squeezes the value into a sliver. */
const Row = ({ label, value, copyable = true, mono = false }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      px: 2,
      py: 1.25,
      borderTop: `1px solid ${UI.line}`,
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: UI.muted,
          fontFamily: FONT,
          lineHeight: 1.4,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          mt: 0.25,
          fontSize: 15,
          fontWeight: 600,
          color: UI.ink,
          fontFamily: mono ? MONO : FONT,
          letterSpacing: mono ? "0.02em" : "normal",
          lineHeight: 1.45,
          overflowWrap: "anywhere",
        }}
      >
        {(mono ? groupDigits(value) : value) || "—"}
      </Typography>
    </Box>
    {copyable && value ? <CopyButton label={label} value={value} /> : null}
  </Box>
);

/** Short non-copyable facts (bank, branch, type) side by side. */
const Fact = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: UI.muted,
        fontFamily: FONT,
        lineHeight: 1.4,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        mt: 0.25,
        fontSize: 14,
        fontWeight: 600,
        color: UI.ink,
        fontFamily: FONT,
        lineHeight: 1.45,
        overflowWrap: "break-word",
      }}
    >
      {value}
    </Typography>
  </Box>
);

const CardTitle = ({ icon, children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.25, bgcolor: UI.surface }}>
    <Box sx={{ color: UI.brand, display: "flex" }}>{icon}</Box>
    <Typography sx={{ fontSize: 13, fontWeight: 700, color: UI.ink, fontFamily: FONT }}>
      {children}
    </Typography>
  </Box>
);

/**
 * Brand World bank account (and optionally the UPI QR). Used on the profile
 * page ("add Brand World as a beneficiary") and on the order payment panel.
 */
const BrandWorldBankDetails = ({ showQr = false, showUpi = true, data: given }) => {
  const query = useBrandWorldPaymentDetails();
  const data = given || query.data;
  const [qrOpen, setQrOpen] = React.useState(false);

  if (!given && query.isLoading) {
    return (
      <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={20} />
      </Box>
    );
  }
  if (!data?.configured) {
    return (
      <Typography sx={{ fontSize: 13, color: "#6B7280", fontFamily: FONT }}>
        Brand World's bank details are not available right now. Please contact
        support.
      </Typography>
    );
  }

  const withQr = showQr && data.qrImageUrl;
  const facts = [
    ["Bank", data.bankName],
    ["Branch", data.branchName],
    ["Account type", data.accountType],
  ].filter(([, v]) => v);

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        alignItems: "flex-start",
        gap: 2,
      }}
    >
      {withQr ? (
        <Box
          sx={{
            flex: "0 1 230px",
            borderRadius: "14px",
            border: `1px solid ${UI.line}`,
            bgcolor: "#fff",
            overflow: "hidden",
            boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
          }}
        >
          <CardTitle icon={<QrCode2RoundedIcon sx={{ fontSize: 18 }} />}>
            Scan &amp; pay with UPI
          </CardTitle>
          <Box sx={{ p: 2, borderTop: `1px solid ${UI.line}`, textAlign: "center" }}>
            <Box
              component="img"
              src={data.qrImageUrl}
              alt="Brand World UPI QR code"
              onClick={() => setQrOpen(true)}
              sx={{
                display: "block",
                width: "100%",
                maxWidth: 196,
                aspectRatio: "1 / 1",
                mx: "auto",
                objectFit: "contain",
                borderRadius: "10px",
                bgcolor: "#fff",
                cursor: "zoom-in",
              }}
            />
            <Button
              size="small"
              startIcon={<ZoomInRoundedIcon sx={{ fontSize: 18 }} />}
              onClick={() => setQrOpen(true)}
              sx={{
                mt: 1,
                px: 1.5,
                borderRadius: "8px",
                textTransform: "none",
                fontFamily: FONT,
                fontSize: 13,
                fontWeight: 600,
                color: UI.brand,
                bgcolor: UI.brandSoft,
                "&:hover": { bgcolor: "#DCE8F6" },
              }}
            >
              View larger QR
            </Button>
            <Typography sx={{ fontSize: 12, color: UI.muted, mt: 1, fontFamily: FONT }}>
              GPay, PhonePe, Paytm or any UPI app
            </Typography>
            {showUpi && data.upiId ? (
              <Box
                sx={{
                  mt: 1.25,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  pl: 1.5,
                  pr: 0.5,
                  py: 0.5,
                  borderRadius: "10px",
                  bgcolor: UI.surface,
                  border: `1px solid ${UI.line}`,
                }}
              >
                <Typography
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 13,
                    fontWeight: 600,
                    color: UI.ink,
                    fontFamily: FONT,
                    textAlign: "left",
                    overflowWrap: "anywhere",
                  }}
                >
                  {data.upiId}
                </Typography>
                <CopyButton label="UPI ID" value={data.upiId} />
              </Box>
            ) : null}
          </Box>
        </Box>
      ) : null}

      <Box
        sx={{
          flex: "1 1 280px",
          minWidth: 0,
          borderRadius: "14px",
          border: `1px solid ${UI.line}`,
          bgcolor: "#fff",
          overflow: "hidden",
          boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
        }}
      >
        <CardTitle icon={<AccountBalanceOutlinedIcon sx={{ fontSize: 18 }} />}>
          Bank transfer · NEFT / RTGS / IMPS
        </CardTitle>
        <Row label="Account name" value={data.accountName || data.companyName} />
        <Row label="Account number" value={data.accountNumber} mono />
        <Row label="IFSC" value={data.ifsc} mono />
        {!withQr && showUpi && data.upiId ? <Row label="UPI ID" value={data.upiId} /> : null}
        {facts.length ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
              columnGap: 2,
              rowGap: 1.25,
              px: 2,
              py: 1.5,
              borderTop: `1px solid ${UI.line}`,
              bgcolor: UI.surface,
            }}
          >
            {facts.map(([label, value]) => (
              <Fact key={label} label={label} value={value} />
            ))}
          </Box>
        ) : null}
      </Box>

      {data.instructions ? (
        <Box
          sx={{
            flex: "1 1 100%",
            display: "flex",
            gap: 1,
            alignItems: "flex-start",
            p: 1.5,
            borderRadius: "10px",
            bgcolor: "#FFFBEB",
            border: "1px solid #FDE68A",
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 18, color: "#B45309", mt: "1px" }} />
          <Typography sx={{ fontSize: 12.5, color: "#92400E", fontFamily: FONT, lineHeight: 1.5 }}>
            {data.instructions}
          </Typography>
        </Box>
      ) : null}

      {withQr ? (
        <Dialog
          open={qrOpen}
          onClose={() => setQrOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: { xs: "16px", sm: "20px" },
              m: 2,
              width: "calc(100% - 32px)",
              boxShadow: "0 24px 48px -12px rgba(15, 23, 42, 0.3)",
            },
          }}
          sx={{ "& .MuiBackdrop-root": { backgroundColor: "rgba(15, 23, 42, 0.6)", backdropFilter: "blur(4px)" } }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2.5, pt: 2, pb: 1.5, borderBottom: `1px solid ${UI.line}` }}>
            <QrCode2RoundedIcon sx={{ fontSize: 20, color: UI.brand }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: UI.ink, fontFamily: FONT, lineHeight: 1.3 }}>
                Scan &amp; pay with UPI
              </Typography>
              {data.accountName || data.companyName ? (
                <Typography noWrap sx={{ fontSize: 12.5, color: UI.muted, fontFamily: FONT }}>
                  Paying {data.accountName || data.companyName}
                </Typography>
              ) : null}
            </Box>
            <IconButton onClick={() => setQrOpen(false)} aria-label="Close" size="small" sx={{ color: UI.muted }}>
              <CloseRoundedIcon />
            </IconButton>
          </Box>
          <DialogContent sx={{ p: { xs: 2, sm: 3 }, textAlign: "center" }}>
            <Box
              component="img"
              src={data.qrImageUrl}
              alt="Brand World UPI QR code (large)"
              sx={{
                display: "block",
                width: "100%",
                maxWidth: 360,
                aspectRatio: "1 / 1",
                mx: "auto",
                objectFit: "contain",
                borderRadius: "12px",
                bgcolor: "#fff",
              }}
            />
            <Typography sx={{ fontSize: 13, color: UI.muted, mt: 1.5, fontFamily: FONT }}>
              Open GPay, PhonePe, Paytm or any UPI app and scan this code.
            </Typography>
            {showUpi && data.upiId ? (
              <Box
                sx={{
                  mt: 1.5,
                  mx: "auto",
                  maxWidth: 360,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  pl: 1.5,
                  pr: 0.5,
                  py: 0.5,
                  borderRadius: "10px",
                  bgcolor: UI.surface,
                  border: `1px solid ${UI.line}`,
                }}
              >
                <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", color: UI.muted, fontFamily: FONT }}>
                  UPI ID
                </Typography>
                <Typography
                  sx={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: UI.ink, fontFamily: FONT, textAlign: "left", overflowWrap: "anywhere" }}
                >
                  {data.upiId}
                </Typography>
                <CopyButton label="UPI ID" value={data.upiId} />
              </Box>
            ) : null}
          </DialogContent>
        </Dialog>
      ) : null}
    </Box>
  );
};

export default BrandWorldBankDetails;
